import type {ChatDocumentType} from '@Libraries/chat-document.types';
import type {AiPromptBoxSelection} from '@Components/ai-prompt-box/components/ai-prompt-box-selection-chip/ai-prompt-box-selection-chip.types';

export interface AiPromptBoxAttachmentsProps {
  id: string;
  onDocumentClick?: (attachment: DocumentAttachment) => void;
  onSelectionRemove?: (selection: AiPromptBoxSelection) => void;
}

export const REFERENCE_IMAGE_RENDER_MAX_DIMENSION = 1024;

export type PromptAttachment = UserImageAttachment | DocumentAttachment | DesignAttachment | ExampleImageAttachment | ReattachedModelImageAttachment;

export enum PendingUploadStatus {
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  FAILED = 'failed',
}

export interface UploadFailure {
  summary: string;
  message: string;
  isRetryable: boolean;
}

export interface PendingUpload {
  blobUrl: string;
  status: PendingUploadStatus;
  failure?: UploadFailure;
}

export type UserImageAttachmentType = PromptAttachmentType.IMAGE | PromptAttachmentType.CAPTURE_PHOTO | PromptAttachmentType.REFERENCE_IMAGE;

export interface UserImageAttachment {
  hashedFilename: string;
  extension: string;
  source: string;
  width?: number;
  height?: number;
  type: UserImageAttachmentType;
  localId?: string;
  pendingUpload?: PendingUpload;
  isRemovable?: boolean;
}

export interface DocumentAttachment {
  type: PromptAttachmentType.DOCUMENT;
  documentHashedId: string;
  filename: string;
  documentType: ChatDocumentType;
  localId?: string;
  pendingUpload?: PendingUpload;
}

export type PendingCapableAttachment = UserImageAttachment | DocumentAttachment;

export type PendingUploadAttachment = PendingCapableAttachment & {localId: string};

export interface ExampleImageAttachment {
  type: PromptAttachmentType.EXAMPLE_IMAGE;
  fileName: string;
}

export interface DesignAttachment {
  designId: string;
  type: PromptAttachmentType.DESIGN | PromptAttachmentType.DESIGN_TEMPLATE;
  previewURL: string;
  pageHashedId?: string;
}

export interface ReattachedModelImageAttachment {
  type: PromptAttachmentType.REATTACHED_MODEL_IMAGE;
  imageHashedId: string;
}

export enum PromptAttachmentType {
  IMAGE = 'image',
  DESIGN = 'design',
  DESIGN_TEMPLATE = 'design-template',
  CAPTURE_PHOTO = 'capture-photo',
  EXAMPLE_IMAGE = 'example-image',
  UPLOAD_FROM_DEVICE = 'upload-from-device',
  REATTACHED_MODEL_IMAGE = 'reattached-model-image',
  REFERENCE_IMAGE = 'reference-image',
  DOCUMENT = 'document',
}

export enum PromptAttachmentDivider {
  HORIZONTAL_LINE = 'horizontal-line',
}

export enum NavigationDirection {
  NEXT = 1,
  PREVIOUS = -1,
}

export type AttachmentDropdownConfig = (PromptAttachmentType | PromptAttachmentDivider)[];

export interface LoadingAttachment {
  id: string;
}

export interface AiPromptBoxAttachments {
  items: PromptAttachment[];
  dropdown: AttachmentDropdownConfig;
  maxAttachments?: number;
  pasteAsReferenceImages?: boolean;
  acceptsDocuments?: boolean;
}
